import React, { useState, useEffect } from 'react';
import { 
  Mail, Send, CheckCircle, XCircle, RefreshCw, 
  AlertTriangle, Settings, Bell, FileText, Clock,
  UserPlus, UserMinus
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailConfigPanel = ({ token }) => {
  const [status, setStatus] = useState(null);
  const [digestStatus, setDigestStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTesting, setIsTesting] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [logs, setLogs] = useState([]);
  const [subscribers, setSubscribers] = useState([]);

  useEffect(() => {
    if (token) {
      checkStatus();
      checkDigestStatus();
      loadLogs();
      loadSubscribers();
    }
  }, [token]);

  const checkStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/email/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStatus(res.data);
    } catch (err) {
      console.error('Error checking email status:', err);
      setStatus({ configured: false, status: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const checkDigestStatus = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/email/digest/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDigestStatus(res.data);
    } catch (err) {
      console.error('Error checking digest status:', err);
    }
  };

  const testConnection = async () => {
    setIsTesting(true);
    try {
      const res = await axios.post(`${API_URL}/api/email/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.status === 'success') {
        toast.success('Connexion SMTP réussie !');
      } else {
        toast.error(res.data.message || 'Erreur de connexion');
      }
      
      setStatus(prev => ({ ...prev, testResult: res.data }));
    } catch (err) {
      toast.error('Erreur lors du test');
    } finally {
      setIsTesting(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Veuillez entrer une adresse email');
      return;
    }
    
    setIsSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/email/test-send`, 
        { to: testEmail },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (res.data.status === 'success') {
        toast.success(`Email de test envoyé à ${testEmail}`);
        loadLogs();
      } else if (res.data.status === 'blocked') {
        toast.warning(res.data.message);
      } else {
        toast.error(res.data.message || 'Erreur d\'envoi');
      }
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur lors de l\'envoi';
      toast.error(msg);
    } finally {
      setIsSending(false);
    }
  };

  const loadLogs = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/email/logs?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLogs(res.data.logs || []);
    } catch (err) {
      console.log('Could not load email logs');
    }
  };

  const loadSubscribers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/email/digest/subscribers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscribers(res.data.subscribers || []);
    } catch (err) {
      console.log('Could not load subscribers');
    }
  };

  const toggleSubscription = async () => {
    setIsSubscribing(true);
    try {
      const endpoint = digestStatus?.user_subscribed 
        ? '/api/email/digest/unsubscribe' 
        : '/api/email/digest/subscribe';
      
      const res = await axios.post(`${API_URL}${endpoint}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.status === 'success') {
        toast.success(res.data.message);
        checkDigestStatus();
        loadSubscribers();
      } else {
        toast.info(res.data.message);
      }
    } catch (err) {
      toast.error('Erreur lors de la modification de l\'abonnement');
    } finally {
      setIsSubscribing(false);
    }
  };

  const sendDigest = async () => {
    setIsSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/email/digest`, 
        { to: testEmail || undefined },
        { headers: { Authorization: `Bearer ${token}` }}
      );
      
      if (res.data.status === 'success') {
        toast.success('Résumé quotidien envoyé');
        loadLogs();
      } else if (res.data.status === 'blocked') {
        toast.warning(res.data.message);
      } else {
        toast.error(res.data.message || 'Erreur d\'envoi');
      }
    } catch (err) {
      toast.error('Erreur lors de l\'envoi du résumé');
    } finally {
      setIsSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="bg-black border-2 border-blue-500/50 p-8 text-center">
        <RefreshCw className="w-8 h-8 text-blue-400 animate-spin mx-auto" />
        <p className="font-mono text-white/90 mt-4">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="bg-black border-2 border-blue-500/50">
      <div className="bg-blue-900/30 px-4 py-3 border-b border-blue-500/50 flex items-center justify-between">
        <span className="font-mono text-blue-400 text-sm uppercase tracking-wider flex items-center gap-2">
          <Mail className="w-4 h-4" />
          Configuration Email & Résumé Quotidien
        </span>
        <button
          onClick={() => { checkStatus(); checkDigestStatus(); loadLogs(); loadSubscribers(); }}
          className="text-blue-400 hover:text-white"
          title="Rafraîchir"
        >
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      <div className="p-4 space-y-6">
        {/* Status */}
        <div className="flex items-center gap-4">
          <div className={`w-3 h-3 rounded-full ${status?.configured ? 'bg-green-500' : 'bg-red-500'}`} />
          <span className="font-mono text-white">
            Service email: {status?.configured ? 'Configuré' : 'Non configuré'}
          </span>
          
          {status?.configured && (
            <button
              onClick={testConnection}
              disabled={isTesting}
              className="ml-auto px-3 py-1 bg-blue-500/20 border border-blue-500 text-blue-400 font-mono text-sm hover:bg-blue-500/30 disabled:opacity-50 flex items-center gap-2"
            >
              {isTesting ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Settings className="w-4 h-4" />
              )}
              Tester connexion
            </button>
          )}
        </div>

        {/* Test Result */}
        {status?.testResult && (
          <div className={`p-3 border ${status.testResult.status === 'success' ? 'border-green-500/50 bg-green-900/20' : 'border-red-500/50 bg-red-900/20'}`}>
            <div className="flex items-center gap-2">
              {status.testResult.status === 'success' ? (
                <CheckCircle className="w-5 h-5 text-green-400" />
              ) : (
                <XCircle className="w-5 h-5 text-red-400" />
              )}
              <span className="font-mono text-sm text-white">{status.testResult.message}</span>
            </div>
            {status.testResult.user && (
              <p className="font-mono text-xs text-white/80 mt-1">Compte: {status.testResult.user}</p>
            )}
          </div>
        )}

        {/* Daily Digest Subscription */}
        {status?.configured && digestStatus && (
          <div className="bg-gradient-to-r from-purple-900/20 to-blue-900/20 border border-purple-500/30 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-purple-400" />
                <span className="font-mono text-purple-400 text-sm uppercase">Résumé Quotidien Automatique</span>
              </div>
              <span className="font-mono text-white/80 text-xs">
                Envoi à {digestStatus.digest_hour} UTC
              </span>
            </div>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="font-mono text-white text-sm">
                  {digestStatus.user_subscribed ? '✅ Vous êtes abonné' : '❌ Non abonné'}
                </p>
                <p className="font-mono text-white/80 text-xs mt-1">
                  {digestStatus.user_email}
                </p>
              </div>
              <button
                onClick={toggleSubscription}
                disabled={isSubscribing}
                className={`px-4 py-2 font-mono text-sm flex items-center gap-2 ${
                  digestStatus.user_subscribed 
                    ? 'bg-red-500/20 border border-red-500 text-red-400 hover:bg-red-500/30'
                    : 'bg-green-500/20 border border-green-500 text-green-400 hover:bg-green-500/30'
                } disabled:opacity-50`}
              >
                {isSubscribing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : digestStatus.user_subscribed ? (
                  <UserMinus className="w-4 h-4" />
                ) : (
                  <UserPlus className="w-4 h-4" />
                )}
                {digestStatus.user_subscribed ? 'Se désabonner' : 'S\'abonner'}
              </button>
            </div>
            
            {subscribers.length > 0 && (
              <div className="mt-3 pt-3 border-t border-purple-500/20">
                <p className="font-mono text-white/80 text-xs">
                  {subscribers.length} abonné(s): {subscribers.map(s => s.user_name).join(', ')}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Warning si non configuré */}
        {!status?.configured && (
          <div className="p-4 bg-amber-900/20 border border-amber-500/50">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-mono text-amber-400 text-sm">Service email non configuré</p>
                <p className="font-mono text-white/80 text-xs mt-1">
                  Les variables SMTP_USER et SMTP_PASSWORD doivent être définies dans le fichier .env du backend.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Send Test Email */}
        {status?.configured && (
          <div className="space-y-4">
            <h3 className="font-mono text-white/90 text-sm uppercase flex items-center gap-2">
              <Send className="w-4 h-4" />
              Envoyer un email de test
            </h3>
            
            <div className="flex gap-3">
              <input
                type="email"
                value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)}
                placeholder="destinataire@example.com"
                className="flex-1 bg-black border border-marine-slate px-3 py-2 font-mono text-white text-sm focus:border-blue-500 outline-none"
              />
              <button
                onClick={sendTestEmail}
                disabled={isSending || !testEmail}
                className="px-4 py-2 bg-blue-500/20 border border-blue-500 text-blue-400 font-mono text-sm hover:bg-blue-500/30 disabled:opacity-50 flex items-center gap-2"
              >
                {isSending ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Mail className="w-4 h-4" />
                )}
                Envoyer test
              </button>
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 gap-3 mt-4">
              <button
                onClick={sendDigest}
                disabled={isSending}
                className="p-3 bg-marine-slate/20 border border-marine-slate hover:border-marine-cyan transition-colors flex items-center gap-3"
              >
                <Bell className="w-5 h-5 text-marine-cyan" />
                <div className="text-left">
                  <p className="font-mono text-white text-sm">Résumé Manuel</p>
                  <p className="font-mono text-white/80 text-xs">Alertes des 24h</p>
                </div>
              </button>
              
              <button
                disabled={true}
                className="p-3 bg-marine-slate/20 border border-marine-slate opacity-50 flex items-center gap-3"
              >
                <FileText className="w-5 h-5 text-marine-green" />
                <div className="text-left">
                  <p className="font-mono text-white text-sm">Envoyer Rapport</p>
                  <p className="font-mono text-white/80 text-xs">Bientôt disponible</p>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* Email Logs */}
        {logs.length > 0 && (
          <div>
            <h3 className="font-mono text-white/90 text-sm uppercase mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Historique des envois
            </h3>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {logs.map((log, idx) => (
                <div 
                  key={idx}
                  className="flex items-center justify-between p-2 bg-marine-slate/10 border border-marine-slate/20"
                >
                  <div className="flex items-center gap-3">
                    {log.status === 'sent' ? (
                      <CheckCircle className="w-4 h-4 text-green-400" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-400" />
                    )}
                    <div>
                      <p className="font-mono text-white text-sm">{log.to}</p>
                      <p className="font-mono text-white/80 text-xs">{log.subject}</p>
                    </div>
                  </div>
                  <span className="font-mono text-white/40 text-xs">
                    {new Date(log.timestamp).toLocaleString('fr-FR')}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailConfigPanel;
